import React, { useState } from 'react';
import {
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ModalConsumer from '../../utility/Modal/ModalConsumer';
import { useAuthStore } from '../../../store/auth.store';
import { useToast } from '../../../contexts/ToastContext';

const useStyles = makeStyles((theme) => ({
  inviteSearchInput: {
    marginBottom: '0 !important',
    '& input': {
      color: 'white',
      backgroundColor: 'transparent',
      minHeight: '32px',
      fontFamily: 'Poppins',
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.1) !important',
        top: 0,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
    },
    '& input::placeholder': {
      color: 'rgba(255,255,255,0.5)',
      opacity: 1,
      fontFamily: 'Poppins',
    }
  }
}));

const ConfirmModal = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    props.callback();
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="delete-confirm-modal">
        <ModalHeader toggle={toggle}>{props.title}</ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button
            className="ml-auto btn-warning-custom"
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Continue</span>
          </Button>
          <Button
            className="ml-2 btn-primary-custom"
            onClick={toggle}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Cancel</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const TableHeader = ({ label, sortKey, orderBy, onSort }) => {
  const isActive = orderBy.key === sortKey;
  const sortIcon = isActive ? (orderBy.direction === 'asc' ? '↓' : '↑') : '↕';

  return (
    <th>
      <h4 onClick={() => onSort(sortKey)} className="mb-0">
        {label} {sortIcon}
      </h4>
    </th>
  );
};

const InvitesList = ({ invites, firebase, orgId, orgName, onRefresh }) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState({ key: 'createdAt', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inviteRows, setInviteRows] = useState([{ email: '', role: 'USER' }]);
  const [newInviteDays, setNewInviteDays] = useState(7);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const auth = useAuthStore((state) => state.auth);
  const { showSuccess, showError } = useToast();

  const handleSort = (key) => {
    setOrderBy(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredInvites = [...invites].filter(invite => {
    if (!searchTerm) return true;
    return (
      invite.invitedEmail?.toLowerCase().includes(searchTerm) ||
      invite.role?.toLowerCase().includes(searchTerm) ||
      invite.code?.toLowerCase().includes(searchTerm)
    );
  });

  const sortedInvites = filteredInvites.sort((a, b) => {
    const aValue = a[orderBy.key] || '';
    const bValue = b[orderBy.key] || '';

    return orderBy.direction === 'asc'
      ? aValue.toString().localeCompare(bValue.toString())
      : bValue.toString().localeCompare(aValue.toString());
  });

  const handleCopyLink = (code) => {
    const link = `${window.location.origin}/invite/${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleDeleteInvite = async (code, email) => {
    try {
      await firebase.deleteInvite(orgId, code);
      showSuccess(`Invite for ${email} has been deleted`);
      onRefresh();
    } catch (err) {
      console.error('Error deleting invite:', err);
      showError(`Error deleting invite: ${err.message}`);
    }
  };

  const handleAddRow = () => {
    setInviteRows(prev => [...prev, { email: '', role: 'USER' }]);
  };

  const handleRemoveRow = (index) => {
    setInviteRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    setInviteRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleCreateInvites = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validRows = inviteRows.filter(row => row.email.trim());

    if (validRows.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    const invalidEmails = validRows.filter(row => !emailRegex.test(row.email.trim()));
    if (invalidEmails.length > 0) {
      setError(`Invalid email: ${invalidEmails[0].email.trim()}`);
      return;
    }

    // Check for duplicates
    const emails = validRows.map(r => r.email.trim().toLowerCase());
    const duplicates = emails.filter((e, i) => emails.indexOf(e) !== i);
    if (duplicates.length > 0) {
      setError(`Duplicate email: ${duplicates[0]}`);
      return;
    }

    setCreating(true);
    setError(null);

    try {
      let successCount = 0;
      const errors = [];

      for (const row of validRows) {
        try {
          await firebase.createOrgInvite(
            orgId,
            orgName,
            auth.uid,
            newInviteDays,
            row.role,
            row.email.trim().toLowerCase()
          );
          successCount++;
        } catch (err) {
          errors.push(`${row.email}: ${err.message}`);
        }
      }

      if (errors.length > 0) {
        showError(`${errors.length} invite(s) failed: ${errors[0]}`);
      }

      if (successCount > 0) {
        showSuccess(`${successCount} invite${successCount > 1 ? 's' : ''} created successfully`);
        setShowCreateModal(false);
        setInviteRows([{ email: '', role: 'USER' }]);
        setNewInviteDays(7);
        onRefresh();
      }
    } catch (err) {
      console.error('Error creating invites:', err);
      setError(err.message || 'Failed to create invites');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const getStatusBadge = (invite) => {
    if (invite.usedBy) {
      return <Badge color="secondary">Used</Badge>;
    }
    if (isExpired(invite.expiresAt)) {
      return <Badge color="warning">Expired</Badge>;
    }
    return <Badge color="success">Active</Badge>;
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'danger';
      case 'OPERATOR': return 'warning';
      default: return 'info';
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search invites by email, role, or code..."
          value={searchTerm}
          onChange={handleSearch}
          className={classes.inviteSearchInput}
          style={{ marginBottom: 0 }}
        />
        <Button
          className="btn-info-custom btn-round"
          onClick={() => setShowCreateModal(true)}
          style={{ whiteSpace: 'nowrap' }}
        >
          <span>Create Invite</span>
        </Button>
      </div>

      <div className="table-scroll-container" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
        <Table className="table-tracks" responsive>
          <thead>
            <tr>
              <TableHeader
                label="Email"
                sortKey="invitedEmail"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="Role"
                sortKey="role"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="Created"
                sortKey="createdAt"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="Expires"
                sortKey="expiresAt"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedInvites.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">
                  No invites found
                </td>
              </tr>
            ) : (
              sortedInvites.map((invite, idx) => (
                <tr key={invite.code} className={idx % 2 === 0 ? 'table-alt-row' : ''}>
                  <td>{invite.invitedEmail}</td>
                  <td>
                    <Badge color={getRoleBadgeColor(invite.role)}>
                      {invite.role}
                    </Badge>
                  </td>
                  <td>{formatDate(invite.createdAt)}</td>
                  <td>{formatDate(invite.expiresAt)}</td>
                  <td>{getStatusBadge(invite)}</td>
                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      {!invite.usedBy && !isExpired(invite.expiresAt) && (
                        <Button
                          color="link"
                          size="sm"
                          className="text-info p-0"
                          onClick={() => handleCopyLink(invite.code)}
                          title="Copy invite link"
                        >
                          {copiedCode === invite.code ? (
                            <i className="tim-icons icon-check-2"></i>
                          ) : (
                            <i className="tim-icons icon-single-copy-04"></i>
                          )}
                        </Button>
                      )}
                      <ModalConsumer>
                        {({ showModal }) => (
                          <Button
                            color="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => {
                              showModal(ConfirmModal, {
                                isOpen: true,
                                title: 'Delete Invite',
                                body: `Are you sure you want to delete the invite for ${invite.invitedEmail}?`,
                                callback: () => {
                                  handleDeleteInvite(invite.code, invite.invitedEmail);
                                },
                              });
                            }}
                            title="Delete invite"
                          >
                            <i className="tim-icons icon-trash-simple"></i>
                          </Button>
                        )}
                      </ModalConsumer>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Create Invite Modal */}
      <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)} contentClassName="delete-confirm-modal" style={{ maxWidth: '600px' }}>
        <ModalHeader toggle={() => setShowCreateModal(false)}>
          Create Invites for {orgName}
        </ModalHeader>
        <ModalBody>
          {error && (
            <div className="text-danger mb-3">{error}</div>
          )}
          <FormGroup style={{ marginBottom: '1rem' }}>
            <Label>Expires In</Label>
            <Input
              type="select"
              value={newInviteDays}
              onChange={(e) => setNewInviteDays(Number(e.target.value))}
            >
              <option value={1}>1 day</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </Input>
          </FormGroup>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <Label style={{ marginBottom: 0 }}>Members</Label>
            <Button
              color="link"
              size="sm"
              onClick={handleAddRow}
              style={{ color: 'var(--accent-1-color)', padding: '0 4px' }}
              title="Add another invite"
            >
              <i className="tim-icons icon-simple-add" style={{ fontSize: '14px' }}></i>
            </Button>
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {inviteRows.map((row, index) => (
              <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={row.email}
                  onChange={(e) => handleRowChange(index, 'email', e.target.value)}
                  style={{ flex: 2 }}
                />
                <Input
                  type="select"
                  value={row.role}
                  onChange={(e) => handleRowChange(index, 'role', e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="USER">USER</option>
                  <option value="OPERATOR">OPERATOR</option>
                  <option value="ADMIN">ADMIN</option>
                </Input>
                {inviteRows.length > 1 && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => handleRemoveRow(index)}
                    style={{ color: 'rgba(255,255,255,0.5)', padding: '0 4px', minWidth: '24px' }}
                    title="Remove"
                  >
                    <i className="tim-icons icon-simple-remove" style={{ fontSize: '12px' }}></i>
                  </Button>
                )}
              </div>
            ))}
          </div>
          <small className="text-muted">
            Each invite will only work for the specified email address
          </small>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-primary-custom" onClick={() => setShowCreateModal(false)}>
            <span>Cancel</span>
          </Button>
          <Button
            className="btn-warning-custom"
            onClick={handleCreateInvites}
            disabled={creating || !inviteRows.some(r => r.email.trim())}
          >
            {creating ? (
              <>
                <Spinner size="sm" className="me-2" />
                <span>Creating...</span>
              </>
            ) : (
              <span>Create {inviteRows.filter(r => r.email.trim()).length > 1 ? `${inviteRows.filter(r => r.email.trim()).length} Invites` : 'Invite'}</span>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default InvitesList;
